import dayjs from 'dayjs'
import { expressURL } from '../../../services/api'

const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

/**
 * Create the Zoho invoice payload object
 * Separated for reuse in bulk export
 */
export const createZohoInvoicePayload = (invoice, zohoState) => {
    // Check if already sent to Zoho
    if (invoice.sentToZoho === true) {
        throw new Error('Invoice already exported to Zoho')
    }

    // Use dates from invoice object
    const dbInvoiceDate = invoice.invoiceDate || invoice.sentDate
    const dbDueDate = invoice.dueDate || dbInvoiceDate

    const CurrentMonthIdx = new Date(dbInvoiceDate).getMonth()
    const currentMonth = months[CurrentMonthIdx]
    const currentYear = dayjs(dbInvoiceDate).format('YYYY')

    // Determine whether organization GSTIN is present
    const hasGSTN =
        zohoState?.organizationGstNo &&
        zohoState.organizationGstNo.trim() !== '' &&
        zohoState.organizationGstNo !== 'N/A'

    // HSN/SAC code for energy services (solar power generation)
    const HSN_SAC_CODE = '27160000'

    // Helper function to create Zoho-compatible invoice number
    const createZohoCompatibleNumber = (originalNumber) => {
        if (!originalNumber) return invoice.invoiceNumber

        // Remove invalid characters and limit to 16 characters
        let validNumber = originalNumber.replace(/[^a-zA-Z0-9\-\/]/g, '')

        // If too long, try to shorten intelligently
        if (validNumber.length > 16) {
            const parts = validNumber.split(/[\-\/]/)
            if (parts.length >= 3) {
                const prefix = parts[0] // INV
                const month = parts[1] // FEB
                const year = parts[2].slice(-2) // 25 (last 2 digits of year)
                const number = parts[parts.length - 1] // 001 (last part)

                validNumber = `${prefix}-${month}${year}-${number}`
            }
        }

        // If still too long, truncate
        if (validNumber.length > 16) {
            validNumber = validNumber.substring(0, 16)
        }

        return validNumber
    }

    // Create a Zoho-compatible invoice number
    const zohoCompatibleNumber = createZohoCompatibleNumber(invoice.invoiceNumber)

    const lineItems = []

    // Helper function to create line item with proper tax handling
    const createLineItem = (
        name,
        description,
        rate,
        quantity,
        unit = 'kWh',
        hsnSacCode
    ) => {
        const lineItem = {
            name,
            description,
            rate: parseFloat(rate || 0),
            quantity: parseFloat(quantity || 0),
            unit,
            item_type: 'service',
        }

        // Add HSN/SAC only when provided (and when GSTIN present)
        if (hsnSacCode) {
            lineItem.hsn_or_sac = hsnSacCode
        }

        // FIXED: Always specify tax exemption for zero-rate items or apply tax for non-zero items
        const itemAmount = lineItem.rate * lineItem.quantity

        if (itemAmount === 0 || lineItem.rate === 0) {
            // For zero-amount items, use tax exemption
            lineItem.tax_exemption_id = 'tax_exempt' // Use appropriate tax exemption ID
            lineItem.tax_percentage = 0
        } else {
            // For non-zero items, determine tax based on location
            const isLocationMatched = zohoState?.isLocationMatched || false

            if (isLocationMatched) {
                // Same state - use CGST + SGST (9% + 9% = 18%)
                lineItem.tax_percentage = 0
                lineItem.cgst_percentage = 0
                lineItem.sgst_percentage = 0
            } else {
                // Different state - use IGST (18%)
                lineItem.tax_percentage = 0
                lineItem.igst_percentage = 0
            }
        }

        return lineItem
    }

    // Add main energy line item with HSN/SAC code
    const energyValu = invoice.energyValue || invoice.energyValu || invoice.actualGeneration || 0
    const mainTariff = invoice.mainTariff || invoice.tariff || 0

    if (parseFloat(energyValu || 0) > 0) {
        lineItems.push(
            createLineItem(
                `${invoice.plantName} (${invoice.plantSize || invoice.plantSizeKW || invoice.plantCapacity || 'N/A'}kWp)`,
                `Energy Generation for ${currentMonth} ${currentYear}`,
                mainTariff || 0,
                energyValu || 0,
                'kWh',
                hasGSTN ? HSN_SAC_CODE : undefined
            )
        )
    }

    // Add deemed generation line item with HSN/SAC code
    const deemedEnergy = invoice.deemedEnergy || invoice.deemedGeneration
    const deemedTariff = invoice.deemedTariff

    if (parseFloat(deemedEnergy || 0) > 0) {
        lineItems.push(
            createLineItem(
                'Deemed Generation',
                `Deemed Energy for ${currentMonth} ${currentYear}`,
                deemedTariff || 0,
                deemedEnergy || 0,
                'kWh',
                hasGSTN ? HSN_SAC_CODE : undefined
            )
        )
    } else {
        // Add zero quantity deemed generation item with tax exemption
        lineItems.push(
            createLineItem(
                'Deemed Generation',
                `Deemed Energy for ${currentMonth} ${currentYear}`,
                0.0,
                0,
                'kWh',
                hasGSTN ? HSN_SAC_CODE : undefined
            )
        )
    }

    // Add O&M charges if applicable
    const omAmounts = invoice.omAmounts || 0
    if (parseFloat(omAmounts || 0) > 0 && invoice.proposalType === 'EMI') {
        lineItems.push(
            createLineItem(
                'O&M Charges',
                `Operations & Maintenance charges for ${currentMonth} ${currentYear}`,
                omAmounts || 0,
                1,
                'month',
                hasGSTN ? '998314' : undefined // SAC code for maintenance and repair services
            )
        )
    }

    const simplifiedNotes = `Subject: Summary of Charges for ${currentMonth} ${currentYear}`

    // Build payload
    const payload = {
        invoice: {
            _id: invoice._id,
            plantId: invoice.plantId, // Explicitly pass plantId
            customer_name: invoice.clientName || invoice.plantName,
            invoice_number: invoice.invoiceNumber,
            custom_invoice_number: zohoCompatibleNumber,
            force_custom_number: true,
            date: dayjs(dbInvoiceDate).format('YYYY-MM-DD'),
            due_date: dayjs(dbDueDate).format('YYYY-MM-DD'),
            line_items: lineItems,
            subject: `Summary of Charges for ${currentMonth} ${currentYear}`,
            notes: simplifiedNotes,
            currency_code: invoice.currency || 'INR',
            tax_configuration: {
                is_location_matched: zohoState?.isLocationMatched || false,
            },
            // Use businessInfo from zohoState if available (enriched from DB or Zoho)
            bank_details: {
                account_name: zohoState?.businessInfo?.accountName || 'Distributed Energy',
                account_number: zohoState?.businessInfo?.accountNumber || '922020010026345',
                bank_name: zohoState?.businessInfo?.nameOfTheBank || 'AXIS Bank',
                ifsc_code: zohoState?.businessInfo?.ifscCode || 'UTIB0003702',
                bank_address: zohoState?.businessInfo?.addressOfTheBank || 'ELDAMS Road',
            },
            totals: {
                current_charges: invoice.invoiceValue || 0,
                outstanding_balance: invoice.outstandingTotal || 0,
                balance_due: (invoice.outstandingTotal || 0) + (invoice.invoiceValue || 0),
                total_billable_units: parseFloat(energyValu || 0),
            },
        },
    }

    if (hasGSTN) {
        payload.invoice.tax_configuration = {
            energy_services: HSN_SAC_CODE,
            maintenance_services: '998314',
        }
    }

    return payload
}

/**
 * Export a single invoice to Zoho Books
 * Matches the logic in PlantBusinessView/components/InvoiceBill/utils/ZohoApis.js
 */
export const exportToZoho = async ({
    invoice,
    zohoState,
}) => {
    try {
        if (!invoice) {
            throw new Error('Invoice data is missing')
        }

        const payload = createZohoInvoicePayload(invoice, zohoState)

        const token = localStorage.getItem('accessToken')
        const response = await fetch(`${expressURL}/zoho/invoices/send`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify(payload),
        })

        const result = await response.json()

        if (!result.success) {
            throw new Error(result.error || 'Unknown error occurred')
        }

        return {
            success: true,
            message: 'Invoice exported to Zoho Books successfully!',
            data: result,
        }
    } catch (error) {
        console.error('Error exporting to Zoho:', error)
        return {
            success: false,
            message: error.message || 'Error exporting to Zoho',
            error: error,
        }
    }
}

/**
 * Fetch Zoho data for an organization
 */
export const getZohoInvoiceDetails = async (organizationId) => {
    try {
        const token = localStorage.getItem('accessToken')
        const response = await fetch(
            `${expressURL}/zoho/data?organizationId=${organizationId}`,
            {
                method: 'GET',
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            }
        )

        if (!response.ok) {
            throw new Error('Failed to fetch invoice details from Zoho')
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching Zoho invoice details:', error)
        throw error
    }
}

/**
 * Helper to prepare zohoState for an invoice
 * Ported from PlantBusinessView.getZohoInvoiceData
 */
export const prepareZohoState = async (invoice) => {
    try {
        const organizationId = invoice.organizationId
        if (!organizationId) return null

        const zohoResponse = await getZohoInvoiceDetails(organizationId)
        if (!zohoResponse || !zohoResponse.data) return null

        const zohoData = zohoResponse.data

        const rawName = zohoData.organization?.name || ''
        const organizationName = rawName.replace(/\s*\(Formerly known as.*?\)\s*/i, '').trim()
        const organizationState = zohoData.organization?.state || ''
        const organizationCountry = zohoData.organization?.country || ''
        const organizationGstNo = zohoData.organization?.gst_number || ''
        const fullAddress = zohoData.organization?.address?.formatted_address || ''

        // Find matching customer
        const matchingCustomer = zohoData.customers?.data?.find(
            (customer) =>
                customer.company_name === invoice.plantName ||
                customer.contact_name === invoice.plantName
        )

        let customerState = ''
        let customerCountry = ''
        let customerGstNo = ''

        if (matchingCustomer) {
            customerState = matchingCustomer.state || ''
            customerCountry = matchingCustomer.country || ''
            customerGstNo = matchingCustomer.gst_number || ''
        }

        const isLocationMatched = organizationState === customerState

        // Extract business info (bank details)
        const businessInfo = zohoData.organization?.businessInfo ||
            zohoData.organization?.billerDetails ||
            zohoData.organization?.raw_additional_sources?.[0]?.data?.businessInfo ||
            {}

        // Find latest invoice address (optional enrichment)
        let latestInvoiceBillingAddress = ''
        const matchingInvoices = zohoData.invoices?.data?.filter(
            (inv) => inv.customer_name === invoice.plantName || inv.company_name === invoice.plantName
        ) || []

        if (matchingInvoices.length > 0) {
            const latestInvoice = matchingInvoices.reduce((latest, current) => {
                return new Date(current.created_time) > new Date(latest.created_time) ? current : latest
            })
            if (latestInvoice && latestInvoice.billing_address) {
                const ba = latestInvoice.billing_address
                latestInvoiceBillingAddress = [ba.address, ba.street2, ba.city, ba.state, ba.zipcode, ba.country]
                    .filter(Boolean).join(', ')
            }
        }

        return {
            organizationName,
            organizationGstNo,
            customerState,
            customerCountry,
            organizationState,
            organizationCountry,
            isLocationMatched,
            orgAddress: fullAddress,
            plantName: invoice.plantName,
            latestInvoiceBillingAddress,
            customerGstNo,
            businessInfo
        }
    } catch (error) {
        console.error('Error preparing Zoho state:', error)
        return null
    }
}
